// src/constants/example-experiences.ts
import { ArchetypeId } from "./archetypes";

export interface ExampleExperience {
  id: string;
  archetype: ArchetypeId;
  name: string;
  title: string;
  label: string;
  description: string;
  videoUrl: string;
  posterUrl: string;
  eyebrow: string;
}

export const EXAMPLE_EXPERIENCES: ExampleExperience[] = [
  // HEARTBEAT
  {
    id: "heartbeat-love-note",
    archetype: "heartbeat",
    name: "Heartbeat",
    title: "For someone you love",
    label: "A personal expression of love, appreciation, or emotional closeness.",
    description: "A warm, intimate experience for someone who matters deeply to you.",
    eyebrow: "Emotional Example",
    videoUrl: "http://res.cloudinary.com/dvikm0fau/video/upload/v1775223441/heartbeat1_jt8tmp.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.44.33_xtqxlo.png",
  },
  {
    id: "heartbeat-appreciation",
    archetype: "heartbeat",
    name: "Heartbeat",
    title: "For someone who means a lot to you",
    label: "A heartfelt way to express care, gratitude, and emotional closeness.",
    description: "A soft, personal experience for someone you genuinely treasure.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223441/heartbeat2_dvovig.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.44.53_jffxm0.png",
  },
  {
    id: "heartbeat-deep-connection",
    archetype: "heartbeat",
    name: "Heartbeat",
    title: "For a deeply personal connection",
    label: "A deeply emotional experience for love, closeness, and intentional expression.",
    description: "A more intimate emotional experience for someone truly close to your heart.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223454/heartbeat3_w1t04c.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.45.11_yyecwb.png",
  },

  // SAFEHAVEN
  {
    id: "safehaven-comfort",
    archetype: "safehaven",
    name: "Safe Haven",
    title: "For comfort, healing, or encouragement",
    label: "A gentle experience for support, reassurance, forgiveness, or peace.",
    description:
      "A calm, reflective experience for someone who needs comfort, care, or reassurance.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223431/haven2_bfsjrj.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.45.48_f7g6tx.png",
  },
  {
    id: "safehaven-reassurance",
    archetype: "safehaven",
    name: "Safe Haven",
    title: "For someone who needs reassurance",
    label: "A soft and calming experience for comfort, emotional safety, and support.",
    description: "A soothing experience for someone going through something difficult.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223447/haven3_a4cmux.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.46.05_utfefu.png",
  },
  {
    id: "safehaven-forgiveness",
    archetype: "safehaven",
    name: "Safe Haven",
    title: "For peace, grace, and reconnection",
    label: "A reflective experience for peace, grace, forgiveness, and emotional restoration.",
    description: "A gentle space for mending, peace, and thoughtful emotional reconnection.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223463/haven1_gtxghm.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224045/Screenshot_2026-04-03_at_16.45.36_dp28xy.png",
  },

  // MOMENTS
  {
    id: "moments-celebration",
    archetype: "moments",
    name: "Moments",
    title: "For a moment worth celebrating",
    label: "A meaningful way to celebrate a milestone, memory, or special occasion.",
    description: "A cinematic celebratory experience for recognition, gratitude, and milestones.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223458/moment1_b0emow.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224046/Screenshot_2026-04-03_at_16.46.21_rfdnwc.png",
  },
  {
    id: "moments-birthday",
    archetype: "moments",
    name: "Moments",
    title: "For a day that matters",
    label: "A memorable experience for birthdays, milestones, and special life moments.",
    description: "A beautiful celebratory experience built around an important day.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223467/moment3_r6uhzk.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224046/Screenshot_2026-04-03_at_16.46.44_ie9jbu.png",
  },
  {
    id: "moments-achievement",
    archetype: "moments",
    name: "Moments",
    title: "For an achievement worth honoring",
    label: "A meaningful way to celebrate effort, growth, and achievement.",
    description: "A polished celebratory experience for someone you are proud of.",
    eyebrow: "Emotional Example",
    videoUrl: "https://res.cloudinary.com/dvikm0fau/video/upload/v1775223466/moment2_d0oiyi.mp4",
    posterUrl:
      "https://res.cloudinary.com/dvikm0fau/image/upload/v1775224046/Screenshot_2026-04-03_at_16.46.31_xnv1rl.png",
  },
];

const ARCHETYPE_ORDER: ArchetypeId[] = ["heartbeat", "safehaven", "moments"];

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

export function getRandomExamplesByArchetype(): ExampleExperience[] {
  return ARCHETYPE_ORDER.map((archetype) => {
    const pool = EXAMPLE_EXPERIENCES.filter((item) => item.archetype === archetype);

    if (pool.length === 0) {
      throw new Error(`No examples found for archetype: ${archetype}`);
    }

    return randomItem(pool);
  });
}
